import type { ReactElement, ReactNode } from 'react'

/**
 * The filter/search strip that sits directly above a table.
 *
 * It is a `<div role="toolbar">`-free zone on purpose: the ARIA toolbar
 * role takes over arrow keys and expects a single tab stop across its
 * children, which is right for an icon bar and wrong for a row of text
 * inputs and selects — an operator typing in a filter would lose arrow-key
 * cursor movement. A labelled `<search>`-style region with ordinary tab
 * order is the honest markup here.
 *
 * It is its own strip with its own border rather than welded to the top
 * of the table. Welding needs the two components to agree on which
 * corners each rounds and which border each drops, and that contract
 * breaks silently — the table renders fine on its own, slightly wrong
 * under a toolbar, and nobody notices for a release.
 */

export interface ToolbarProps {
  /** Filters, search boxes, tabs — anything that narrows what the table
   * below shows. Left-aligned, wraps on a narrow viewport. */
  children: ReactNode
  /** Actions that act on the view as a whole — refresh, export, "New".
   * Right-aligned. */
  actions?: ReactNode
  /** Names the region for assistive tech. Needed when a page has more
   * than one table, which several of these screens do. */
  label?: string
}

export function Toolbar({
  children,
  actions,
  label = 'Filters',
}: ToolbarProps): ReactElement {
  return (
    <div
      role="group"
      aria-label={label}
      className="mb-2 flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-surface px-2 py-1.5"
    >
      <div className="flex flex-wrap items-center gap-2">{children}</div>
      {actions !== undefined && (
        <div className="flex items-center gap-2">{actions}</div>
      )}
    </div>
  )
}
